import { CalendarIcon } from "Components/Icons/CalendarIcon";
import { StrategicPlanParent, StrategicPlanStore } from "Stores/strategicPlanStore";
import React, { useMemo, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import { StatusType } from "./strategicPlans";
import ControlledInput from "Components/Common/ControlledInput";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { EAIClient } from "Services/EAIClient";
import { ItemContainer } from "./ItemContainer";
import { PlanItemFormData, PlanFormData, ApiResponse } from "./interfaces";
import getFormatedDate from "../../../utils/dateUtils";
import { StyledModal } from "Components/Common/StyledModal";

interface CreateStrategicPlanFormProps {
    strategicPlan?: StrategicPlanParent;
    showAlert?: (message: string) => void;
    disable?: boolean;
    strategicPlanStore?: StrategicPlanStore;
    handleCloseCreateItem: () => void
    loadStrategicPlans: () => Promise<void>;
    showProcessing: (e: boolean) => void;
}

const CreateStrategicPlanFormContext = React.createContext<CreateStrategicPlanFormProps | Partial<CreateStrategicPlanFormProps>>({});

const StatusCircle = ({ type, value }: { type: StatusType, value: number }) => {
    return (
        <div className={`${type} status-circle`}>
            {value}
        </div>
    );
};

export const CreateStrategicPlanForm = ({
    loadStrategicPlans,
    handleCloseCreateItem,
    showAlert,
    showProcessing
}: CreateStrategicPlanFormProps) => {
    const toggleButtonRef = useRef<Record<string, HTMLButtonElement | null>>({});
    const [datePeriod, setDatePeriod] = useState<{ start: Date | null, end: Date | null }>(
        {
            start: null,
            end: null
        });
    // const [hasFormError, setHasFormError] = useState(false);
    // const [errorMessage, setErrorMessage] = useState("An error has occurred. Please try again.");
    const [showDuplicatedValue, setShowDuplicatedValue] = useState(false);

    const api = new EAIClient();

    const generateUniqueId = () => {
        return Math.floor(Math.random() * Date.now());
    };

    const defaultDataForm: PlanItemFormData = {
        level: 0,
        school_id: "",
        name: "",
        status: "active",
        strategic_plan_ruuid: "",
        created: "",
        updated: "",
        display_order: 1000000000,
        id: generateUniqueId()
    };
    const [strategicPlans, setStrategicPlans] = useState<PlanItemFormData[]>([defaultDataForm]);

    const getDisplayOrder = (siblings: PlanItemFormData[], parentItem?: PlanItemFormData) => {
        const level = parentItem ? parentItem.level + 1 : 0;
        const parentDisplayOrder = parentItem ? parentItem.display_order : 0;
        let lastOrder = 0;
        let displayOrder = 0;
        if (level === 0) {
            displayOrder = 1000000000;
        } else if (level === 1) {
            displayOrder = 1000000;
        } else if (level === 2) {
            displayOrder = 1000;
        } else {
            displayOrder = 1;
        }
        if (siblings.length >0) {
            lastOrder = siblings[siblings.length-1].display_order;
            if (lastOrder) {
                return lastOrder + displayOrder;
            }
        }
        return displayOrder + parentDisplayOrder;
    };


    const handleAddItem = (itemId: number, parentId: number) => {
        const newStrategicPlans = [...strategicPlans] as PlanItemFormData[];
        const findItemById = (id: number, items: PlanItemFormData[], depth = 0): PlanItemFormData | undefined => {
            if (depth > 4) {
                return undefined;
            }
            for (const item of items) {
                if (item.id === id) {
                    return item;
                }
                if (item.children && item.children.length > 0) {
                    const foundItem = findItemById(id, item.children, depth + 1);
                    if (foundItem) {
                        return foundItem;
                    }
                }
            }
            return undefined;
        };

        const findItemWithChildId = (item: PlanItemFormData, childId: number): PlanItemFormData | undefined => {
            if (item.children) {
                for (const child of item.children) {
                    if (child.id === childId) {
                        return item;
                    }
                    const foundItem = findItemWithChildId(child, childId);
                    if (foundItem) {
                        return foundItem;
                    }
                }
            }
            return undefined;
        };
        if (parentId === itemId) {
            const newItem: PlanItemFormData = {
                id: generateUniqueId(),
                level: 0,
                children: [],
                school_id: "",
                name: "",
                status: "active",
                strategic_plan_ruuid: "",
                display_order: getDisplayOrder(newStrategicPlans),
                created: "",
                updated: "",
            };
            newStrategicPlans.push(newItem);
        } else {
            const parentItem = findItemById(parentId, newStrategicPlans);
            if (parentItem) {
                const parentOfItemId = findItemWithChildId(parentItem, itemId);
                const newItem: PlanItemFormData = {
                    id: generateUniqueId(),
                    level: parentOfItemId!.level + 1,
                    children: [],
                    school_id: "",
                    name: "",
                    status: "active",
                    strategic_plan_ruuid: "",
                    display_order: getDisplayOrder(parentItem.children || [], parentItem),
                    created: "",
                    updated: "",
                };
                if (parentOfItemId && parentOfItemId.children) {
                    parentOfItemId.children.push(newItem);
                }
            }
        }
        setStrategicPlans(newStrategicPlans);
    };

    const handleDeleteItem = (itemId: number) => {
        const newStrategicPlans = [...strategicPlans] as PlanItemFormData[];
        if (newStrategicPlans.length === 1 && newStrategicPlans[0].id === itemId) {
            return;
        }
        const deleteRecursive = (items: PlanItemFormData[]): PlanItemFormData[] => {
            return items.reduce((acc: PlanItemFormData[], item: PlanItemFormData) => {
                if (item.id === itemId) {
                    unregisterChildren(item.children);
                    return acc;
                }
                const newItem: PlanItemFormData = {
                    ...item,
                    children: deleteRecursive(item.children || []),
                };
                return [...acc, newItem];
            }, []);
        };
        const unregisterChildren = (children: PlanItemFormData[] = []) => {
            children.forEach((child) => {
                unregisterChildren(child.children);
            });
        };
        const updatedItems = deleteRecursive(newStrategicPlans);
        setStrategicPlans(updatedItems);
    };

    const handleAddChild = (parentId: number, parentLevel: number) => {
        if (parentLevel > 2) return;

        const newStrategicPlans = [...strategicPlans] as PlanItemFormData[];
        const findItemById = (id: number, items: PlanItemFormData[]): PlanItemFormData | undefined => {
            for (const item of items) {
                if (item.id === id) {
                    return item;
                }
                if (item.children && item.children.length > 0) {
                    const foundItem = findItemById(id, item.children);
                    if (foundItem) {
                        return foundItem;
                    }
                }
            }
            return undefined;
        };
        const parentItem = findItemById(parentId, newStrategicPlans);
        if (parentItem && parentItem.level > 2) return;
        if (parentItem) {
            const newItem: PlanItemFormData = {
                id: generateUniqueId(),
                level: parentItem.level + 1,
                children: [],
                school_id: "",
                name: "",
                status: "",
                strategic_plan_ruuid: "",
                display_order: getDisplayOrder(parentItem.children || [], parentItem),
                created: "",
                updated: "",
            };
            parentItem.children = parentItem.children || [];
            parentItem.children.push(newItem);
            setStrategicPlans(newStrategicPlans);
            setTimeout(() => {
                toggleButtonRef.current[parentItem.id]?.click();
            }, 10);
        }
    };

    const methods = useForm<PlanFormData>();

    const createButton = document.getElementById("saveChanges") as HTMLButtonElement | null;
    if (methods.formState.isDirty && methods.formState.isValid && !!datePeriod.end && !!datePeriod.start) {
        if (createButton) createButton.disabled = false;
        createButton?.classList.add("ready");
    } else {
        if (createButton) createButton.disabled = true;
        createButton?.classList.remove("ready");
    }

    const sendRequestToAPI = async (strategicPlanId: string, parentId: number | null, name: string, displayOrder: number): Promise<{ success: boolean; data: ApiResponse }> => {
        const res = await api.createStrategicPlanItem(strategicPlanId, {
            name: name,
            status: "active",
            parent_id: parentId || null,
            display_order: displayOrder,
        });
        return { success: true, data: res.data };
    };

    const createPlan = async (plans: PlanItemFormData[], strategicPlanId: string, parentId: number | null, data: PlanFormData) => {
        for (const plan of plans) {
            try {
                const response = await sendRequestToAPI(strategicPlanId, parentId, data[`${plan.id}#input`], plan.display_order);
                if (plan.children && plan.children.length > 0) {
                    await createPlan(plan.children, strategicPlanId, response.data.data.id, data);
                }
                await loadStrategicPlans();
                handleCloseCreateItem();
                if (showAlert) {
                    showAlert("The changes have been saved.");
                    setTimeout(() => showAlert(""), 2500);
                }
            } catch (error) {
                if (showAlert) {
                    showAlert(error.response.data.result.error === "Conflict" ?
                        "One of the items you created was omitted due to a duplicated name, try again." :
                        "An error has occurred. Try again later.");
                    setTimeout(() => showAlert(""), 3500);
                }
            }
        }
    };

    const submitForm: SubmitHandler<PlanFormData> = async (data) => {
        showProcessing(true);
        try {
            const response = await api.createStrategicPlan({
                name: data.strategic_plan_name,
                start_date: getFormatedDate(data.start_date),
                end_date: getFormatedDate(data.end_date),
            });
            await createPlan(strategicPlans, response.data.data.ruuid, null, data);
            showProcessing(false);
        } catch (error) {
            showProcessing(false);
            if (createButton) createButton.disabled = true;
            createButton?.classList.remove("ready");
            if (error.response.data.result.code === 409) {
                setShowDuplicatedValue(true);
            }
        }
    };


    const strategicPlanItems = useMemo(() => {
        return strategicPlans.map((item, index) => {
            return (
                <div key={index + item.level}>
                    <ItemContainer
                        toggleButtonRef={toggleButtonRef}
                        controlledInput
                        hideArchive={true}
                        required
                        inputName={`${item?.id}#input`}
                        level={item.level}
                        onOpenDelete={() => handleDeleteItem(item?.id)}
                        onAddItem={() => handleAddItem(item?.id, item.id)}
                        onAddSubItem={() => {
                            handleAddChild(item?.id, item.level);
                            toggleButtonRef.current[item?.id]?.click();
                        }}
                        eventKey={`${item.id}`}
                        enableMenu={!!item.children?.length}
                        isEdit={true} />
                    {item.children &&
                        item.children.map((child) => (
                            <Accordion.Collapse eventKey={`${item.id}`} key={child.id} >
                                <Accordion >
                                    <ItemContainer
                                        toggleButtonRef={toggleButtonRef}
                                        controlledInput
                                        hideArchive={true}
                                        required inputName={`${child?.id}#input`}
                                        level={child.level}
                                        onOpenDelete={() => handleDeleteItem(child?.id)}
                                        onAddSubItem={() => {
                                            handleAddChild(child?.id, child.level);
                                            toggleButtonRef.current[child?.id]?.click();
                                        }}
                                        onAddItem={() => handleAddItem(child?.id, item.id)}
                                        eventKey={`${child.id}`}
                                        enableMenu={!!child.children?.length}
                                        isEdit={true} />
                                    {child.children &&
                                        child.children.map((secondChild) => (
                                            <Accordion.Collapse eventKey={`${child.id}`} key={secondChild.id}>
                                                <Accordion >
                                                    <ItemContainer
                                                        toggleButtonRef={toggleButtonRef}
                                                        level={secondChild.level}
                                                        hideArchive={true}
                                                        controlledInput
                                                        required
                                                        inputName={`${secondChild?.id}#input`}
                                                        onOpenDelete={() => handleDeleteItem(secondChild?.id)}
                                                        onAddSubItem={() => {
                                                            handleAddChild(secondChild?.id, secondChild.level);
                                                            toggleButtonRef.current[secondChild?.id]?.click();
                                                        }}
                                                        onAddItem={() => handleAddItem(secondChild?.id, child.id)}
                                                        eventKey={`${secondChild.id}`}
                                                        enableMenu={!!secondChild.children?.length}
                                                        isEdit={true} />
                                                    {secondChild.children &&
                                                        secondChild.children.map((thirdChild) => (
                                                            <Accordion.Collapse eventKey={`${secondChild.id}`} key={thirdChild.id}>
                                                                <ItemContainer
                                                                    toggleButtonRef={toggleButtonRef}
                                                                    level={thirdChild.level}
                                                                    hideArchive={true}
                                                                    controlledInput
                                                                    required
                                                                    inputName={`${thirdChild?.id}#input`}
                                                                    eventKey={`${thirdChild.id}`}
                                                                    onOpenDelete={() => handleDeleteItem(thirdChild?.id)}
                                                                    onAddItem={() => handleAddItem(thirdChild?.id, secondChild.id)}
                                                                    enableMenu={!!thirdChild.children?.length}
                                                                    isEdit={true} />
                                                            </Accordion.Collapse>
                                                        ))}
                                                </Accordion>
                                            </Accordion.Collapse>
                                        ))}
                                </Accordion>
                            </Accordion.Collapse>
                        ))
                    }
                </div>
            );
        });
    }, [strategicPlans]);

    return (
        <CreateStrategicPlanFormContext.Provider value={{}}>
            <Accordion defaultActiveKey={""} className="strategic-plan-dropdown-container">
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(submitForm)} id="new-strategic-plan-form">
                        <div className="general-data-container">
                            <div className="not-status">
                                <div className="title-icons">
                                    <div className="strategic-plan-name" >
                                        {/* <CustomToggle eventKey={renderableData?.ruuid ?? "0"} disabled={!renderableData?.items.length} /> */}
                                        <ControlledInput
                                            className={"input-strategic-plans"}
                                            name={"strategic_plan_name"}
                                            placeholder={"Plan - Example"}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="vertical-divider" />
                                <div className="strategic-plan-date">
                                    <CalendarIcon className="drop-icon me-2" />
                                    <ReactDatePicker
                                        placeholderText="Start Date"
                                        className="form-control me-2"
                                        onChange={(start) => {
                                            setDatePeriod({ start, end: datePeriod.end });
                                            start && (methods.setValue("start_date", start));
                                        }}
                                        maxDate={datePeriod.end}
                                        selected={datePeriod.start}
                                    />
                                    <CalendarIcon className="drop-icon me-2" />
                                    <ReactDatePicker
                                        placeholderText="End Date"
                                        className="form-control me-2"
                                        onChange={(end) => {
                                            setDatePeriod({ start: datePeriod.start, end });
                                            end && (methods.setValue("end_date", end));
                                        }}
                                        minDate={datePeriod.start}
                                        selected={datePeriod.end}
                                    />
                                </div>
                                <div className="vertical-divider" />
                            </div>
                            <StatusCircle type={"open"} value={0} />
                            <StatusCircle type={"achieved"} value={0} />
                            <StatusCircle type={"unmet"} value={0} />
                            <StatusCircle type={"no_date_assigned"} value={0} />
                            <StatusCircle type={"overdue"} value={0} />
                        </div>
                        {strategicPlanItems}
                    </form>
                </FormProvider>
            </Accordion>
            <StyledModal
                isOpen={showDuplicatedValue}
                confirmButtonLabel={"Got it"}
                onClose={() => setShowDuplicatedValue(false)}
                title={"Duplicated name"}
                subtitle={"Seems like the plan name has already been used. Try with another."}
                onSubmit={(e) => {
                    setShowDuplicatedValue(false);
                    e.preventDefault();
                }}
            />
        </CreateStrategicPlanFormContext.Provider>
    );
};